import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Choice from '@components/choice/Choice'
import Tag from '@components/tag/Tag'
import Icon from '@components/icon/Icons'
import SimpleAlert from '@components/modal/SimpleAlert'
import Select from '@components/select/Select'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as IncidentStore from '@store/incident/incident'
import * as SiteStore from '@store/site/site'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'

import useBreakpoint from '@utils/useBreakpoints'

interface Props {
  onItemSelected: (item?: Partial<IncidentForm>) => void
  onQuit: () => void
}

const Start = ({ onItemSelected, onQuit }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const forms = useReducer(IncidentStore.store, (s) => s.forms)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations.inProgress)
  const site = useReducer(SiteStore.store, (s) => s.site)

  const previous = React.useMemo(
    () =>
      forms
        .filter((f) => !!site && f.siteId === site.id)
        .sort((a, b) => b.creationDate!.localeCompare(a.creationDate!)),
    [forms, site]
  )

  const onDelete = (id: string) => {
    SimpleAlert.open({
      title: i18n.t('common.delete'),
      description: i18n.t('screens.incident.deleting'),
      buttons: [
        {
          label: i18n.t('common.yes'),
          onClick: () => {
            IncidentStore.actions.removeForm(id)
            IncidentStore.actions.resetForm()

            SimpleAlert.close()
          },
        },
        { label: i18n.t('common.no'), onClick: () => SimpleAlert.close() },
      ],
    })
  }

  const renderItem = (item: Partial<IncidentForm>) => {
    const manif = manifestations.find((m) => m.id === item.manifestationId)
    const acts = (item.familles || [])
      .map((ac) => activities.find((a) => a.id === ac))
      .filter(Boolean) as FamilleResponse[]
    const space = places.find((m) => m.id === item.space)
    const location = places.find((m) => m.id === item.location)
    const subLocation = places.find((m) => m.id === item.subLocation)

    const loc = [!!space && space.libelle, !!location && location.libelle, !!subLocation && subLocation.libelle]
      .filter(Boolean)
      .join(' - ')

    return (
      <Item key={item.id} activeOpacity={0.9} onPress={() => onItemSelected(item)}>
        <ItemBloc>
          <Manif>{manif ? manif.libelle : i18n.t('screens.incident.MANIF.empty')}</Manif>
          <Location>{loc}</Location>

          {bp !== 'phone' && (
            <Delete activeOpacity={0.9} onPress={() => onDelete(item.id || '')}>
              <Icon name="delete" size={24} color={theme.colors.primary} />
            </Delete>
          )}
        </ItemBloc>

        <Separator />

        <ItemBloc>
          {!!item.isInfrastructure && (
            <Info>{i18n.t(`screens.incident.TYPE.${item.isInfrastructure.toLowerCase()}`)}</Info>
          )}
          {acts.length > 0 && <Activity>{acts.map((a) => a.libelle).join(' / ')}</Activity>}
          {!!item.creationDate && (
            <Info>{i18n.t('screens.incident.START.date', { date: new Date(item.creationDate) })}</Info>
          )}
        </ItemBloc>
      </Item>
    )
  }

  if (bp === 'phone') {
    return (
      <ScrollView contentContainerStyle={{ padding: 15, flexGrow: 1 }}>
        <Group>
          <SubTitle>{i18n.t('screens.incident.START.new')}</SubTitle>
          <Select label={i18n.t('screens.incident.START.startNew')} onClick={() => onItemSelected()} icon="arrow" />
        </Group>

        <SubTitle>{i18n.t('screens.incident.START.finish')}</SubTitle>

        {previous.length > 0 ? (
          previous.map(renderItem)
        ) : (
          <MissingInfo>{i18n.t('screens.incident.START.noIncident')}</MissingInfo>
        )}
      </ScrollView>
    )
  }

  return (
    <DualLine>
      <Column left>
        <Title>{i18n.t('screens.incident.START.title')}</Title>
      </Column>
      <Column>
        <Content>
          <Animators>
            <Group>
              <SubTitle>{i18n.t('screens.incident.START.new')}</SubTitle>
              <Choice
                data={[{ label: i18n.t('screens.incident.START.startNew'), value: 'new' }]}
                onItemSelected={() => onItemSelected()}
              />
            </Group>

            <Group>
              <SubTitle>{i18n.t('screens.incident.START.finish')}</SubTitle>
              {previous.length > 0 ? (
                <ScrollView>{previous.map(renderItem)}</ScrollView>
              ) : (
                <MissingInfo>{i18n.t('screens.incident.START.noIncident')}</MissingInfo>
              )}
            </Group>
          </Animators>
        </Content>
      </Column>

      <TagContainer>
        <Tag
          activeColor
          focused
          item={{ label: i18n.t('screens.incident.quit'), value: 'tag' }}
          leftIcon="close"
          onItemSelected={onQuit}
        />
      </TagContainer>
    </DualLine>
  )
}

export default Start

const DualLine = styled(View)`
  flex: 1;
  flex-direction: row;
`

const Column = styled(ScrollView)<{ left?: boolean }>`
  flex: 1;
  background-color: ${(props) => (props.left ? props.theme.colors.cardSecondary : props.theme.colors.background)};
  padding: 25px;
  padding-top: 175px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
`

const Content = styled(View)`
  margin: 0 20px;
`

const Group = styled(View)`
  margin-bottom: ${(props) => (props.theme.breakpoint === 'phone' ? 25 : 45)}px;
`

const SubTitle = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Headline : props.theme.fonts.Title1)};
  margin-bottom: 8px;
`

const TagContainer = styled(View)`
  position: absolute;
  top: 25px;
  left: 25px;
`

const Item = styled(TouchableOpacity)`
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.secondaryActive};
  border-radius: 6px;
  margin-bottom: 10px;
  position: relative;
`

const ItemBloc = styled(View)`
  padding: 16px 24px;
  justify-content: center;
`

const Manif = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const Location = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 14px;
`

const Activity = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  font-size: 16px;
`

const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
`

const Delete = styled(TouchableOpacity)`
  position: absolute;
  right: 20px;
  padding: 10px;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
